// The client's link to the server: connect, hello (as a saved character), reconnect, pings and network stats.
// The account comes from the sign-in cookie, which the browser sends with the socket.
import { PING_MS, PROTOCOL_VERSION } from '../../../shared/constants.js';
import { JsonCodec, byteLength, type Codec } from '../../../shared/codec.js';
import type { ClientMsg, ServerMsg } from '../../../shared/protocol.js';
import { UNRELIABLE } from '../../../shared/protocol.js';
import type { Transport } from '../../../shared/transport.js';
import { connectBest } from './transports.js';

export interface Identity { character: number; quiet?: () => boolean }

export class Connection {
  private t: Transport | null = null;
  readonly codec: Codec = JsonCodec;
  status: 'connecting' | 'online' | 'reconnecting' | 'stopped' = 'connecting';
  transportNote = '';
  // stats
  rtt = 0; jitter = 0; private rtts: number[] = [];
  bytesIn = 0; bytesOut = 0; kbIn = 0; kbOut = 0; private statT = performance.now();
  private pingTimer = 0; private retry = 0; everOnline = false;
  onMessage: (m: ServerMsg) => void = () => {};
  onStatus: (s: Connection['status'], why?: string) => void = () => {};

  constructor(private readonly id: Identity) {}

  get kind(): string { return this.t?.kind ?? 'none'; }

  private urls(): { ws: string; wt: string | null } {
    const q = new URLSearchParams(location.search), fake = new URLSearchParams();
    for (const k of ['lag', 'jitter', 'loss']) if (q.get(k)) fake.set(k, q.get(k)!);
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    return { ws: `${proto}//${location.host}/ws${fake.toString() ? '?' + fake : ''}`, wt: null };
  }

  async start(): Promise<void> {
    this.status = this.retry ? 'reconnecting' : 'connecting'; this.onStatus(this.status);
    try {
      const u = this.urls(), { t, note } = await connectBest(u.ws, u.wt);
      this.t = t; this.transportNote = note;
      t.onMessage = d => { this.bytesIn += byteLength(d); this.handle(this.codec.decode(d) as ServerMsg); };
      t.onClose = why => this.lost(why);
      this.send({ type: 'hello', v: PROTOCOL_VERSION, character: this.id.character, quiet: this.id.quiet?.() ?? false });
      clearInterval(this.pingTimer);
      this.pingTimer = window.setInterval(() => this.send({ type: 'ping', t: performance.now() }), PING_MS);
    } catch { this.lost('could not connect'); }
  }

  private handle(m: ServerMsg): void {
    if (m.type === 'welcome') { this.everOnline = true; this.retry = 0; this.status = 'online'; this.onStatus('online'); this.send({ type: 'ping', t: performance.now() }); }
    else if (m.type === 'pong') {
      const r = performance.now() - m.t; this.rtts.push(r); if (this.rtts.length > 20) this.rtts.shift();
      this.rtt = r; const mean = this.rtts.reduce((a, b) => a + b, 0) / this.rtts.length;
      this.jitter = Math.sqrt(this.rtts.reduce((a, b) => a + (b - mean) ** 2, 0) / this.rtts.length);
    } else if (m.type === 'error' && (m.code === 'version' || m.code === 'bad_hello' || m.code === 'full' || m.code === 'auth')) { this.status = 'stopped'; this.onStatus('stopped', m.msg); }
    this.onMessage(m);
  }

  send(m: ClientMsg): void {
    const t = this.t; if (!t) return;
    const d = this.codec.encode(m); this.bytesOut += byteLength(d);
    if (UNRELIABLE.has(m.type)) t.sendUnreliable(d); else t.sendReliable(d);
  }

  private lost(why: string): void {
    clearInterval(this.pingTimer); this.t = null;
    if (this.status === 'stopped') return;
    if (why.includes('4002')) { this.status = 'stopped'; this.onStatus('stopped', 'You are playing this character in another tab.'); return; }
    if (why.includes('4003') || why.includes('4001')) { this.status = 'stopped'; this.onStatus('stopped', 'The server refused this character. Please refresh.'); return; }
    this.status = 'reconnecting'; this.onStatus('reconnecting', this.everOnline ? why : 'Can\'t reach the server yet. Retrying…');
    const wait = Math.min(8000, 400 * 2 ** this.retry++);
    setTimeout(async () => {
      // a socket refused for a missing sign-in looks like any other failure to the browser: ask directly
      if (this.retry >= 2) { try { const r = await fetch('/api/me'); if (!(await r.json()).signedIn) { this.status = 'stopped'; this.onStatus('stopped', 'Please sign in again.'); return; } } catch { /* offline: keep trying */ } }
      if (this.status !== 'stopped') this.start();
    }, wait);
  }

  /** Bandwidth, refreshed about once a second. */
  tickStats(): void {
    const now = performance.now(), dt = (now - this.statT) / 1000;
    if (dt < 1) return;
    this.kbIn = this.bytesIn / 1024 / dt; this.kbOut = this.bytesOut / 1024 / dt; this.bytesIn = this.bytesOut = 0; this.statT = now;
  }

  leave(): void { this.status = 'stopped'; this.send({ type: 'leave' }); this.t?.close(); }
}
